import {
  FormInput,
  ModalFooter,
  ModalHeader,
  SimpleModal
} from '@ami/react/core';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { NotificationManager } from 'react-notifications';
import { QuoteProductDto } from '../../../quotes/otherQuoteEntities';
import { QuoteProductConfiguration } from '../../../quotes/quoteProductConfiguration';
import LeadTimeBreakdownComponent from '../../step3/LeadTimeBreakdownComponent';
import LeadTimeOverrideService from './LeadTimeOverrideService';
import { isAmiUserAuthorizedToOverrideQuoteLeadTimes } from '@ami/react/shared';

export default class LeadTimeDetailsModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      show: false,
      saving: false,
      overridesList: [],
      removeOverridesList: [],
      originalLeadTime: null,
      originalOverriddenSubComponents: [],
      message: ''
    };
  }

  static propTypes = {
    quoteProduct: PropTypes.instanceOf(QuoteProductDto).isRequired,
    quoteId: PropTypes.string,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration).isRequired,
    isEditMode: PropTypes.bool,
    isOverrideMode: PropTypes.bool,
    onLeadTimeOverrideUpdated: PropTypes.func.isRequired,
    refreshQuote: PropTypes.func
  };

  componentDidMount() {
    const { quoteProduct } = this.props;
    this.storeDefaultValues(quoteProduct.leadTime);
  }

  storeDefaultValues(leadTime) {
    if (!leadTime)
      return;

    this.setState({ originalLeadTime: leadTime, overridesList: [], removeOverridesList: [] });

    const overriddenProductSubComponents = leadTime.product?.subComponents?.filter(x => x.isOverridden) || [];
    const overriddenProductOptionSubComponents = leadTime.productOptions?.flatMap(x => x.subComponents).filter(x => x.isOverridden) || [];
    const originalOverriddenSubComponents = [...overriddenProductSubComponents, ...overriddenProductOptionSubComponents];
    this.setState({ originalOverriddenSubComponents, originalLeadTime: leadTime });
  }

  show() {
    this.setState({ show: true });
  }

  hide = () => {
    this.setState({ show: false });
    this.updateQuoteProductLeadTimeOverridesInput();
  };

  findOriginalSubComponentByKey(key) {
    const { originalLeadTime } = this.state;

    const productSubComponent = originalLeadTime.product.subComponents.find(x => x.key === key);
    if (productSubComponent)
      return productSubComponent;

    const productOptionSubComponent = originalLeadTime.productOptions.flatMap(x => x.subComponents).find(x => x.key === key);
    return productOptionSubComponent;
  }

  overrideLeadTime = (updatedItem, leadTime) => {
    const { originalOverriddenSubComponents, overridesList, removeOverridesList } = this.state;

    const originalSubComponent = this.findOriginalSubComponentByKey(updatedItem.key);
    if (!originalSubComponent) {
      NotificationManager.error('Lead Time not found');
      return;
    }

    if ((updatedItem.value === updatedItem.overrideValue && updatedItem.availableOn === updatedItem.overrideAvailableOn) || (originalSubComponent.value === updatedItem.overrideValue && originalSubComponent.availableOn === updatedItem.overrideAvailableOn)) {
      NotificationManager.error('No changes made');
      return;
    }

    const removedItemIndex = removeOverridesList.findIndex(x => x.key === updatedItem.key);
    if (removedItemIndex > -1) {
      removeOverridesList.splice(removedItemIndex, 1);
    }

    const existingItem = originalOverriddenSubComponents.find(x => x.key === updatedItem.key);
    if (!existingItem || existingItem.value !== updatedItem.overrideValue || existingItem.availableOn !== updatedItem.overrideAvailableOn) {
      this.addUpdatedItemToOverridesList(updatedItem);
    }

    this.emitLeadTimeOverrideUpdatedEvent(leadTime);
    this.updateQuoteProductLeadTimeOverridesInput();
  };

  addUpdatedItemToOverridesList(updatedItem) {
    const { overridesList } = this.state;
    const existingItemIndex = overridesList.findIndex(x => x.key === updatedItem.key);
    if (existingItemIndex > -1) {
      overridesList.splice(existingItemIndex, 1);
    }
    overridesList.push(updatedItem);
  }

  removeLeadTimeOverride = (removedItem, leadTime) => {
    const { originalOverriddenSubComponents, overridesList, removeOverridesList } = this.state;
    const isItemOverridden = !!originalOverriddenSubComponents.find(x => x.key === removedItem.key);

    if (isItemOverridden) {
      const removedItemIndex = removeOverridesList.findIndex(x => x.key === removedItem.key);
      if (removedItemIndex === -1)
        removeOverridesList.push(removedItem);
    }

    const overriddenItemIndex = overridesList.findIndex(x => x.key === removedItem.key);
    if (overriddenItemIndex > -1)
      overridesList.splice(overriddenItemIndex, 1);

    this.emitLeadTimeOverrideUpdatedEvent(leadTime);
    this.updateQuoteProductLeadTimeOverridesInput();
  };

  updateQuoteProductLeadTimeOverridesInput = () => {
    const { quoteId, quoteProduct } = this.props;
    const { message } = this.state;
    if (quoteId)
      return;

    quoteProduct.updateProductLeadTimeOverride((this.getLeadTimeOverridesInput() || []), message);
  }

  getLeadTimeOverridesInput = () => {
    const { overridesList } = this.state;
    return overridesList.map(x => ({ key: x.key, value: x.overrideValue ?? x.value, unit: x.unit, availableOn: x.overrideAvailableOn ?? x.availableOn }));
  }

  emitLeadTimeOverrideUpdatedEvent(leadTime) {
    this.props.onLeadTimeOverrideUpdated(leadTime);
    this.forceUpdate();
  }

  cancelChanges = () => {
    const { originalLeadTime } = this.state;
    this.storeDefaultValues(originalLeadTime);
    this.emitLeadTimeOverrideUpdatedEvent(originalLeadTime);
  }

  saveChanges = async () => {
    const { quoteProduct, quoteId } = this.props;
    const { overridesList, removeOverridesList, message } = this.state;

    if (overridesList.length === 0 && removeOverridesList.length === 0)
      NotificationManager.error('No changes made');
    if (!message)
      NotificationManager.error('Please provide a reason for changes');

    this.setState({ saving: true });

    try {
      const leadTimeOverrides = this.getLeadTimeOverridesInput();
      const removedLeadTimeOverrideKeys = removeOverridesList.map(x => x.key);
      const leadTimeOutput = await LeadTimeOverrideService.updateLeadTimeOverrides(quoteId, quoteProduct.id, { leadTimeOverrides, removedLeadTimeOverrideKeys, reason: message });

      this.setState({ saving: false, originalLeadTime: leadTimeOutput, message: '' });
      this.storeDefaultValues(leadTimeOutput);
      this.emitLeadTimeOverrideUpdatedEvent(leadTimeOutput);
      this.props.refreshQuote();
      this.hide();
    } catch (ex) {
      this.setState({ saving: false });
    }
  }

  render() {
    const { quoteProduct, quoteProductConfiguration, isEditMode, isOverrideMode, quoteId } = this.props;
    const { overridesList, removeOverridesList } = this.state;
    const hasPendingChanges = overridesList.length > 0 || removeOverridesList.length > 0;
    const isQuoteView = !!quoteId;

    return (
      <SimpleModal show={this.state.show}>
        <ModalHeader title="Lead Time Details" />

        {hasPendingChanges && (
          <div className="row">
            <div className="col-12 py-1 px-3">
              <div className="alert alert-danger d-flex py-1 px-2 mb-0">
                <div className="justify-content-start">
                  <i className="bi bi-exclamation-diamond-fill fs-5 text-danger p-2"></i>
                </div>
                <div className="justify-content-start align-self-center">You have unsaved changes to lead times</div>
              </div>
            </div>
          </div>
        )}

        <LeadTimeBreakdownComponent
          quoteProduct={quoteProduct}
          quoteId={quoteId}
          quoteProductConfiguration={quoteProductConfiguration}
          isEditMode={isEditMode}
          isOverrideMode={isOverrideMode}
          onLeadTimeOverridden={this.overrideLeadTime}
          onLeadTimeRemoved={this.removeLeadTimeOverride}
        />

        {hasPendingChanges && (
          <div className='row'>
            <div className='col-12 px-4'>
              <FormInput
                id="message"
                label="Reason"
                type="textarea"
                placeholder="Enter a reason"
                value={this.state.message}
                onChange={(e) => this.setState({ message: e.target.value })}
                required
              />
            </div>
          </div>
        )}

        <ModalFooter>
          {!isQuoteView && (
            <button className="btn btn-secondary" onClick={this.hide}>
              Close
            </button>
          )}
          {isQuoteView && (
            <Fragment>
              {!hasPendingChanges && (
                <button className="btn btn-secondary" onClick={this.hide}>
                  Close
                </button>
              )}

              {hasPendingChanges && (
                <Fragment>
                  <button className="btn btn-secondary me-2" onClick={this.cancelChanges}>
                    Cancel
                  </button>

                  {isAmiUserAuthorizedToOverrideQuoteLeadTimes() && (
                    <button className="btn btn-primary" disabled={this.state.saving || !this.state.message} onClick={this.saveChanges} >
                      Save
                    </button>
                  )}
                </Fragment>
              )}
            </Fragment>
          )}

        </ModalFooter>
      </SimpleModal>
    )
  }
}
